<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';
import { TransitionChild, TransitionRoot } from '@headlessui/vue';
import { type ModalV2, type ModalV2Props, useModalStore } from './modal-store';
import IconButton from '../IconButton/IconButton.vue';
import {
  type ModalCloseGuard,
  setModalCloseGuardKey,
} from './modal-close-guard';

const props = defineProps<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modal: ModalV2<ModalV2Props<any>>;
}>();

const modalStore = useModalStore();
const closeGuard = ref<ModalCloseGuard | null>(null);

provide(setModalCloseGuardKey, (guard: ModalCloseGuard | null) => {
  closeGuard.value = guard;
});

const isTopModal = computed(() => {
  const topModal = modalStore.modals.value[modalStore.modals.value.length - 1];
  return topModal.id === props.modal.props.id;
});

async function closeModal() {
  if (!isTopModal.value) {
    return;
  }

  if (closeGuard.value) {
    const shouldClose = await closeGuard.value();
    if (!shouldClose) {
      return;
    }
  }

  props.modal.props.resolve({
    value: undefined,
    cancelled: true,
  });
}

const isOpen = computed(() => {
  const modalObject = modalStore.modals.value.find(
    (m) => m.id === props.modal.props.id,
  );
  return modalObject?.isOpen;
});

const viewProps = computed<Record<string, unknown>>(
  () => (props.modal.props as Record<string, unknown> | undefined) ?? {},
);

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isTopModal.value) {
    void closeModal();
  }
}

onMounted(() => document.addEventListener('keydown', onKeyDown));
onUnmounted(() => document.removeEventListener('keydown', onKeyDown));
</script>

<template>
  <Teleport to="body">
    <TransitionRoot appear :show="isOpen">
      <div class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25" @click="closeModal" />
        </TransitionChild>
        <div class="fixed inset-0">
          <div class="flex h-full items-center justify-center text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <div
                class="flex max-h-full w-full min-w-0 transform flex-col rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
                :class="[
                  props.modal.size === 'sm' && 'max-w-sm p-6',
                  props.modal.size === 'md' && 'max-w-lg p-6',
                  props.modal.size === 'lg' && 'max-w-3xl p-6',
                  props.modal.size === 'xl' &&
                    'h-full max-w-full rounded-none p-6 sm:h-[90vh] sm:max-w-6xl sm:rounded-2xl',
                  props.modal.size === 'full' && 'h-full max-w-full p-6',
                  props.modal.size === 'entire-screen' &&
                    'h-full max-w-full rounded-none p-0',
                ]"
              >
                <div class="flex min-h-0 min-w-0 flex-1 flex-col">
                  <div
                    v-if="
                      props.modal.title && props.modal.size !== 'entire-screen'
                    "
                    class="mb-4 flex items-center"
                  >
                    <h3 class="flex-1 text-2xl font-bold">
                      {{ props.modal.title }}
                    </h3>
                    <IconButton
                      variant="ghost-neutral"
                      icon="cancel"
                      @click="closeModal"
                    />
                  </div>
                  <div class="flex min-h-0 min-w-0 flex-1">
                    <component :is="props.modal.component" v-bind="viewProps" />
                  </div>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </div>
    </TransitionRoot>
  </Teleport>
</template>
